// Tjenestekatalogen fra backenden, som erstatter Hygraph (planer/hygraph-plan.md). Samme
// funksjoner og samme form på svarene som den gamle src/lib/hygraph.js, så sidene som bruker dem
// ikke trengte å endres.
//
// Innlogget henter ansatt-appen hele katalogen (GET /tjenestekatalog/ansatt), med tjenester som er
// slått av og de som bare finnes i butikken (f.eks. trykk). Uten innlogging brukes den offentlige.
// Den hentes én gang per KATALOG_MS, så flere komponenter på samme side deler ett kall. Feiler
// kallet, gir funksjonene tomme svar som før.
import { getToken } from "../auth";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");
const KATALOG_MS = 30 * 1000;

let katalog = null; // { tid, verdi }
let pagaende = null;

export function glemKatalog() {
  katalog = null;
}

async function hentKatalog() {
  if (katalog && Date.now() - katalog.tid < KATALOG_MS) return katalog.verdi;
  if (pagaende) return pagaende;
  pagaende = (async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/tjenestekatalog${getToken() ? "/ansatt" : ""}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const verdi = await res.json();
      katalog = { tid: Date.now(), verdi };
      return verdi;
    } catch (err) {
      console.error("[tjenestekatalog] Klarte ikke hente katalogen:", err?.message || err);
      return null;
    } finally {
      pagaende = null;
    }
  })();
  return pagaende;
}

/* ---------- Som i den gamle hygraph.js ---------- */

export async function getTjenester(groupSlug = "") {
  const slug = String(groupSlug || "").trim();
  const grupper = (await hentKatalog())?.tjenestes || [];
  return slug ? grupper.filter((g) => g.slug === slug) : grupper;
}

export async function getServiceBySlug(slug) {
  const services = (await hentKatalog())?.services || [];
  return services.find((s) => s.slug === slug) || null;
}

// Bookingtypene som er tatt i bruk på sykkeltjenestene (feltet «booking»), samlet. En type som
// ingen tjeneste har (f.eks. tidliguke/senuke før de er tilgjengeliggjort), kan ikke bookes.
// Som før i Hygraph: alle sykkeltjenester, også de som er slått av.
export async function getSykkelBookingtyper() {
  const services = (await hentKatalog())?.services || [];
  return services.filter((s) => s.kategori === "sykkelservice").flatMap((s) => s.booking || []);
}

const SERVICETYPE = {
  sykkelservice: "Sykkelservice",
  skiservice: "Skiservice",
  skoyteslip: "Skøyteslip",
  tekstiltrykk: "Tekstiltrykking",
};

// Tjenestene i katalogen, normalisert til formen FixedPrice hadde
// (_id/title/price/description/serviceType/priority), som registreringssidene forventer.
// Beskrivelsene fra den gamle fastprislisten ligger nå i katalogen (scripts/migrer-fastpriser-til-katalog.js).
function somArbeid(r) {
  return {
    _id: r.id,
    title: r.tjeneste,
    description: r.tjenestebeskrivelse || "",
    price: r.pris || 0,
    tidsbruk: r.tidsbruk || 0,
    serviceType: SERVICETYPE[r.kategori] || r.kategori,
    priority: typeof r.sortering === "number" ? r.sortering : 9999,
    kunIButikk: !!r.kunIButikk,
    // «Alpint», «Langrenn» eller begge (som på roykensykkelservice.no/ski-skoyte); tom for skøyter.
    gren: (r.tjenestetyper || []).map((t) => t.tjenestetype).filter(Boolean),
    // aktiv=false skjules; null (aldri satt) regnes som aktiv, som på nettsiden.
    aktiv: r.aktiv !== false,
    leveringstidDager: typeof r.leveringstidDager === "number" ? r.leveringstidDager : null,
    samlesUkentlig: !!r.samlesUkentlig,
    tillattButikkIds: r.tillattButikkIds || [],
  };
}

export async function getSkiSkoyteTjenester() {
  const rows = ((await hentKatalog())?.services || []).filter((r) => r.kategori === "skiservice" || r.kategori === "skoyteslip");
  return rows.map(somArbeid);
}

// Arbeidslista for sykkel- og trykkregistreringen (ServiceDetailsBike/Club): bare tjenester som
// er slått på.
export async function getArbeidsliste(kategori) {
  return ((await hentKatalog())?.services || [])
    .filter((r) => r.kategori === kategori && r.aktiv !== false)
    .map(somArbeid);
}

export async function getButikkKapasiteter() {
  const rows = (await hentKatalog())?.butikkKapasitets || [];
  return rows
    .filter((r) => r && r.slug)
    .map((r) => ({ ...r, lokasjonsNavn: r?.lokasjonsNavn || "" }))
    .filter((r) => r.lokasjonsNavn.trim().length > 0);
}

export async function getKapasitetsOverstyringer() {
  return (await hentKatalog())?.kapasitetsOverstyrings || [];
}

// Sjekkpunktene for tjenestene (WorkshopDetails). Bare for ansatte.
export async function getChecklistsForServiceSlugs(slugs = []) {
  if (!Array.isArray(slugs) || !slugs.length) return [];
  try {
    const res = await fetch(`${API_BASE_URL}/tjenestekatalog/sjekklister?slugs=${encodeURIComponent(slugs.join(","))}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (err) {
    console.error("[tjenestekatalog] Klarte ikke hente sjekklister:", err?.message || err);
    return [];
  }
}
